/* BattleHudV2 — Clash-Royale-style HUD for LiveArena.
 *
 * Replaces the old top strip + stacked creatures + separate log row
 * with:
 *   1) A VS Banner at the top showing both fighters' portraits, names
 *      and HP bars flanking a central "VS" ring.
 *   2) A side-by-side ground stage where creatures face each other in
 *      the center of the viewport (challenger left, opponent right).
 *   3) A 3-button action dock at the bottom (Ability | primary paw
 *      Tap Zone | Consumable/Ultimate) for one-thumb play.
 *
 * All sizing uses clamp()/svh so the scene feels the same on iPhone SE
 * as on the Pro Max — no fixed dimensions.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Zap, Sparkles, ArrowLeft, Settings2, FlaskConical } from 'lucide-react';
import {
  PERFECT_COOLDOWN_MS, PERFECT_WINDOW_START_MS, PERFECT_WINDOW_END_MS,
} from '../../lib/liveArenaEngine';


/* -------- Portrait circle used inside the VS banner ------------------ */
function PortraitCircle({ url, ring = '#4ade80', size = 44 }) {
  return (
    <div
      className="relative shrink-0 rounded-full overflow-hidden"
      style={{
        width: size, height: size,
        boxShadow: `0 0 0 2px ${ring}, 0 0 12px ${ring}88`,
      }}
    >
      {url ? (
        <img src={url} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-black/40" />
      )}
    </div>
  );
}


/* -------- VS Banner - top HP strip w/ portraits + name + hp bar ----- */
export function VsBanner({ chSide, opSide, timeLeft }) {
  return (
    <div className="flex items-center gap-2" data-testid="vs-banner">
      {/* Challenger side (left) */}
      <FighterTag side={chSide} align="left" ringColor="#4ade80" testid="vs-ch" />
      {/* VS emblem — small on mobile so we don't steal HP-bar space */}
      <div className="relative shrink-0 flex flex-col items-center">
        <div
          className="rounded-full flex items-center justify-center border-2 border-amber-400/70 shadow-[0_0_18px_-4px_rgba(251,191,36,0.8)]"
          style={{
            width: 'clamp(38px, 10vw, 46px)',
            height: 'clamp(38px, 10vw, 46px)',
            background: 'radial-gradient(circle at 30% 30%, rgba(251,191,36,0.3), rgba(0,0,0,0.7))',
          }}
        >
          <span className="font-serif font-bold text-white text-sm tracking-widest">VS</span>
        </div>
        {timeLeft != null && (
          <span className="mt-0.5 text-[9px] font-mono text-amber-200 tabular-nums">{fmtTime(timeLeft)}</span>
        )}
      </div>
      {/* Opponent side (right) */}
      <FighterTag side={opSide} align="right" ringColor="#f87171" testid="vs-op" />
    </div>
  );
}


function FighterTag({ side, align, ringColor, testid }) {
  const pct = side?.hp != null ? Math.max(0, Math.min(1, side.hp)) * 100 : 100;
  const name = side?.name || '—';
  const url = side?.snap?.picture || side?.snap?.image_url;
  const bar = (
    <div className="flex-1 min-w-0">
      <p
        className={`font-serif text-xs sm:text-sm leading-tight text-white truncate drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)] ${align === 'right' ? 'text-right' : 'text-left'}`}
      >
        {name}
      </p>
      <div className="relative h-2.5 rounded-md overflow-hidden bg-black/70 border border-white/10 mt-0.5">
        <motion.div
          className="absolute inset-y-0 left-0"
          style={{
            background: `linear-gradient(90deg, ${ringColor}, ${ringColor}aa)`,
            boxShadow: `0 0 6px ${ringColor}`,
          }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        />
        <span
          className="absolute inset-0 text-[9px] font-mono text-white/90 flex items-center justify-center tabular-nums drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]"
          style={{ pointerEvents: 'none' }}
        >
          {Math.round(pct)} HP
        </span>
      </div>
    </div>
  );
  return (
    <div className="flex-1 min-w-0 flex items-center gap-1.5" data-testid={testid}>
      {align === 'left' && <PortraitCircle url={url} ring={ringColor} size={40} />}
      {bar}
      {align === 'right' && <PortraitCircle url={url} ring={ringColor} size={40} />}
    </div>
  );
}


/* -------- Rune sigil floor decoration - "battle ring" beneath fighters */
export function BattleSigil() {
  return (
    <svg
      className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
      style={{
        bottom: 'clamp(20px, 6svh, 60px)',
        width: 'clamp(160px, 46vw, 260px)',
        height: 'clamp(30px, 8svh, 60px)',
        opacity: 0.55,
        mixBlendMode: 'screen',
      }}
      viewBox="0 0 260 60"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="sigil-fade" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#E9C46A" stopOpacity="0.9" />
          <stop offset="1" stopColor="#E9C46A" stopOpacity="0" />
        </linearGradient>
      </defs>
      <ellipse cx="130" cy="30" rx="120" ry="22" fill="none"
                stroke="url(#sigil-fade)" strokeWidth="1.2" strokeDasharray="6 4" />
      <ellipse cx="130" cy="30" rx="88" ry="16" fill="none"
                stroke="url(#sigil-fade)" strokeWidth="0.8" strokeDasharray="2 3" />
      <ellipse cx="130" cy="30" rx="46" ry="8" fill="none"
                stroke="url(#sigil-fade)" strokeWidth="0.6" />
    </svg>
  );
}


/* -------- 3-button action dock at bottom of the arena --------------- */
export function ActionDock({
  onTap, onHoldStart, onHoldEnd, tapEnergy, tapCost, tapFrozen,
  abilities = [], onFireAbility,
  consumables = [],
  ultimateCharge = 0, ultimateFired = false, onFireUltimate,
  reducedMotion,
  perfectCooldownStart = 0, perfectStreak = 0, perfectFlashKey = 0,
}) {
  const primaryAbility = abilities.find((a) => a.ready) || abilities[0];
  const primedItem = consumables[0];
  const ultReady = ultimateCharge >= 60 && !ultimateFired;

  // Local RAF loop for the perfect-tap cooldown ring so the reducer
  // doesn't have to fire on every animation frame.
  const [ringPct, setRingPct] = useState(0);
  const rafRef = useRef(0);
  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    if (!perfectCooldownStart) { setRingPct(0); return; }
    const start = perfectCooldownStart;
    const loop = () => {
      const dt = Date.now() - start;
      const p = Math.min(100, (dt / PERFECT_COOLDOWN_MS) * 100);
      setRingPct(p);
      if (dt < PERFECT_COOLDOWN_MS) rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [perfectCooldownStart]);
  const windowLo = (PERFECT_WINDOW_START_MS / PERFECT_COOLDOWN_MS) * 100;
  const windowHi = (PERFECT_WINDOW_END_MS / PERFECT_COOLDOWN_MS) * 100;
  const inSweetSpot = ringPct >= windowLo && ringPct <= windowHi;

  return (
    <div className="flex items-end justify-between gap-3 shrink-0 px-1" data-testid="action-dock">
      {/* Left side button — primary ability if any */}
      <SideDockButton
        icon={<Zap className="w-5 h-5" strokeWidth={2} />}
        label={primaryAbility?.name ? primaryAbility.name.split(' ')[0] : 'Ability'}
        ring="#67e8f9"
        active={!!primaryAbility?.ready}
        disabled={!primaryAbility?.ready}
        onClick={onFireAbility}
        testid="dock-ability-btn"
      />

      {/* Center primary — the giant paw-print tap zone */}
      <button
        onClick={onTap}
        onPointerDown={onHoldStart}
        onPointerUp={onHoldEnd}
        onPointerLeave={onHoldEnd}
        disabled={tapFrozen}
        data-testid="dock-primary-btn"
        data-perfect-window={inSweetSpot ? 'true' : 'false'}
        aria-label="Strike"
        className="relative rounded-full flex items-center justify-center select-none touch-manipulation disabled:opacity-50"
        style={{
          width: 'clamp(78px, 21vw, 96px)',
          height: 'clamp(78px, 21vw, 96px)',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {/* Outer ring */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              'radial-gradient(circle at 30% 25%, rgba(251,191,36,0.55), rgba(251,191,36,0.05) 65%)',
            boxShadow: inSweetSpot
              ? '0 0 32px 0 rgba(251,191,36,1), inset 0 0 20px rgba(251,191,36,0.55)'
              : '0 0 30px -4px rgba(251,191,36,0.85), inset 0 0 20px rgba(251,191,36,0.3)',
            border: inSweetSpot
              ? '3px solid rgba(253,224,71,1)'
              : '2.5px solid rgba(251,191,36,0.75)',
            transition: 'box-shadow 0.12s ease, border-color 0.12s ease',
          }}
        />
        {/* Paw glyph */}
        <PawIcon className={`relative w-10 h-10 ${inSweetSpot ? 'text-yellow-100' : 'text-amber-100'} ${reducedMotion || tapFrozen ? '' : 'animate-pulse'}`} />

        {/* Perfect-Tap cooldown ring — sweet-spot arc + moving playhead.
           The arc is drawn beneath the energy micro-ring so a heavy hitter
           can still see how close they are to the next attack window. */}
        {perfectCooldownStart > 0 && ringPct < 100 && (
          <svg viewBox="0 0 100 100" className="absolute inset-0 pointer-events-none"
               style={{ transform: 'rotate(-90deg)' }} aria-hidden>
            {/* Sweet-spot arc (static amber band) */}
            <circle cx="50" cy="50" r="49" fill="none"
              stroke="rgba(253,224,71,0.35)" strokeWidth="2"
              strokeDasharray={`${((windowHi - windowLo) / 100) * 307} 307`}
              strokeDashoffset={`${-((windowLo) / 100) * 307}`}
              strokeLinecap="round" />
            {/* Progress arc */}
            <circle cx="50" cy="50" r="49" fill="none"
              stroke={inSweetSpot ? 'rgba(253,224,71,1)' : 'rgba(52,211,153,0.85)'}
              strokeWidth="3"
              strokeDasharray={`${(ringPct / 100) * 307} 307`}
              strokeLinecap="round" />
          </svg>
        )}

        {/* Energy micro-ring */}
        {tapCost > 1 && (
          <svg viewBox="0 0 100 100" className="absolute inset-0 pointer-events-none"
            style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="50" cy="50" r="46" fill="none"
              stroke="rgba(251,191,36,0.85)" strokeWidth="3"
              strokeDasharray={`${(tapEnergy / tapCost) * 289} 289`}
              strokeLinecap="round" />
          </svg>
        )}

        {/* Perfect-Tap flash — pops on every successful perfect */}
        <AnimatePresence>
          {perfectFlashKey > 0 && (
            <motion.span
              key={perfectFlashKey}
              initial={{ opacity: 0, y: 10, scale: 0.7 }}
              animate={{ opacity: 1, y: -14, scale: 1 }}
              exit={{ opacity: 0, y: -30, scale: 1.1 }}
              transition={{ duration: 0.55 }}
              data-testid="perfect-tap-flash"
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full px-2 py-0.5 rounded-full bg-amber-400 text-black text-[10px] font-bold uppercase tracking-widest whitespace-nowrap shadow-[0_0_14px_rgba(251,191,36,0.9)]"
            >
              Perfect!
            </motion.span>
          )}
        </AnimatePresence>

        {/* Streak counter — shows once player chains 2+ perfect taps */}
        {perfectStreak > 1 && (
          <span
            data-testid="perfect-streak"
            className="absolute -bottom-1 left-1/2 -translate-x-1/2 translate-y-full px-1.5 py-[1px] rounded-full bg-amber-500/30 border border-amber-300/60 text-amber-100 text-[9px] font-mono uppercase tracking-widest"
          >
            {perfectStreak}× streak
          </span>
        )}
      </button>

      {/* Right side button — ultimate if charged, else consumable */}
      {ultReady ? (
        <SideDockButton
          icon={<Sparkles className="w-5 h-5" strokeWidth={2} />}
          label="Ult"
          ring="#facc15"
          active={true}
          onClick={onFireUltimate}
          testid="dock-ultimate-btn"
          pulse={!reducedMotion}
        />
      ) : primedItem ? (
        <SideDockButton
          icon={<FlaskConical className="w-5 h-5" strokeWidth={2} />}
          label={(primedItem.name || 'Item').split(' ')[0]}
          ring="#a78bfa"
          active={true}
          disabled={true}
          onClick={() => {}}
          testid="dock-consumable-btn"
        />
      ) : (
        <SideDockButton
          icon={<Sparkles className="w-5 h-5 opacity-40" strokeWidth={2} />}
          label={`${Math.round(ultimateCharge)}%`}
          ring="#e5e7eb"
          active={false}
          disabled={true}
          onClick={() => {}}
          testid="dock-ultimate-charging"
        />
      )}
    </div>
  );
}


function SideDockButton({ icon, label, ring, active, disabled, onClick, testid, pulse }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`relative flex flex-col items-center gap-1 select-none touch-manipulation ${disabled ? 'opacity-60' : ''}`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <div
        className={`rounded-full flex items-center justify-center ${pulse ? 'animate-pulse' : ''}`}
        style={{
          width: 'clamp(52px, 14vw, 64px)',
          height: 'clamp(52px, 14vw, 64px)',
          background: active
            ? `radial-gradient(circle at 30% 25%, ${ring}55, rgba(0,0,0,0.7))`
            : 'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.06), rgba(0,0,0,0.7))',
          border: `2px solid ${active ? ring : 'rgba(255,255,255,0.15)'}`,
          boxShadow: active ? `0 0 18px -4px ${ring}` : 'none',
          color: active ? '#fff' : 'rgba(255,255,255,0.4)',
        }}
      >
        {icon}
      </div>
      <span className="text-[9px] font-semibold uppercase tracking-widest text-white/80">
        {label}
      </span>
    </button>
  );
}


/* -------- Paw print SVG icon --------------------------------------- */
function PawIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <ellipse cx="12" cy="16" rx="5.5" ry="4.5" />
      <ellipse cx="5" cy="10.5" rx="2.2" ry="3" />
      <ellipse cx="19" cy="10.5" rx="2.2" ry="3" />
      <ellipse cx="8.5" cy="5.5" rx="1.8" ry="2.4" />
      <ellipse cx="15.5" cy="5.5" rx="1.8" ry="2.4" />
    </svg>
  );
}


function fmtTime(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
